import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { ShieldAlert, Scale, Info, Mail, Clock } from "lucide-react";
import { generateBreadcrumbSchema, generateWebPageSchema } from "@/lib/utils";

const SITE_URL = "https://letslistbusiness.com";

export const Route = createFileRoute("/disclaimer/")({
  head: () => {
    const title = "Disclaimer | LetsListBusiness";
    const description = "Read our disclaimer regarding the accuracy of information and third-party content on the LetsListBusiness platform.";
    const url = SITE_URL + "/disclaimer/";
    
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: "Disclaimer" },
      ],
      links: [{ rel: "canonical", href: url }],
      schema: [
        generateWebPageSchema(title, description, url),
        generateBreadcrumbSchema([
          { label: "Home", href: "/" },
          { label: "Disclaimer", href: "/disclaimer/" },
        ], SITE_URL),
      ],
    };
  },
  component: DisclaimerPage,
});

function DisclaimerPage() {
  const lastUpdated = "April 24, 2026";

  return (
    <>
      <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Disclaimer" }]} />

      {/* Hero Header */}
      <section className="container mx-auto px-4 pb-16 pt-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary mb-6">
            <ShieldAlert className="h-8 w-8" />
          </div>
          <h1 className="font-display text-5xl font-bold leading-tight sm:text-7xl">
            Legal <em className="not-italic text-primary">Disclaimer.</em>
          </h1>
          <p className="mt-6 text-xl text-muted-foreground">
            Please read this information carefully to understand our independent status and content policies.
          </p>
          <div className="mt-8 flex items-center justify-center gap-2 text-sm font-medium text-muted-foreground">
            <Clock className="h-4 w-4" />
            Last updated: {lastUpdated}
          </div>
        </div>
      </section>

      {/* Content Layout — Sophisticated Document Style */}
      <section className="container mx-auto px-4 pb-32">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-12">
          
          {/* Sticky Sidebar Navigation */}
          <aside className="lg:col-span-4">
            <div className="sticky top-24 space-y-4 rounded-3xl border border-border bg-surface p-8 shadow-sm">
              <h3 className="font-display text-lg font-bold">In this document</h3>
              <nav className="flex flex-col gap-3 text-sm">
                <a href="#affiliation" className="flex items-center gap-2 text-muted-foreground transition-colors hover:text-primary">
                  <span className="h-1 w-1 rounded-full bg-primary" /> Independent Status
                </a>
                <a href="#intellectual-property" className="flex items-center gap-2 text-muted-foreground transition-colors hover:text-primary">
                  <span className="h-1 w-1 rounded-full bg-primary" /> Intellectual Property
                </a>
                <a href="#accuracy" className="flex items-center gap-2 text-muted-foreground transition-colors hover:text-primary">
                  <span className="h-1 w-1 rounded-full bg-primary" /> Accuracy & Liability
                </a>
                <a href="#contact" className="flex items-center gap-2 text-muted-foreground transition-colors hover:text-primary">
                  <span className="h-1 w-1 rounded-full bg-primary" /> Contact & Rights
                </a>
              </nav>
              <div className="mt-8 pt-8 border-t border-border">
                 <p className="text-xs text-muted-foreground leading-relaxed">
                   By accessing and using this website, you acknowledge and agree to the terms outlined in this Disclaimer.
                 </p>
              </div>
            </div>
          </aside>

          {/* Main Document Content */}
          <div className="lg:col-span-8">
            <div className="prose prose-lg prose-slate max-w-none">
              
              <div id="affiliation" className="mb-16">
                <div className="flex items-center gap-3 mb-4">
                   <div className="h-8 w-8 rounded-lg bg-foreground text-background flex items-center justify-center">
                     <Scale className="h-4 w-4" />
                   </div>
                   <h2 className="text-2xl font-bold font-display m-0">Independent Publication</h2>
                </div>
                <p className="text-lg text-foreground/80 leading-relaxed">
                  This website is an independent publication and is not affiliated with, endorsed by, sponsored by, or associated with any previous owner, company, organization, brand, or trademark holder that may have previously owned, operated, or been connected with this domain name.
                </p>
                <p className="mt-4 text-lg text-foreground/80 leading-relaxed">
                  All content published on this website is original and created solely for this platform unless otherwise stated.
                </p>
              </div>

              <div id="intellectual-property" className="mb-16">
                <div className="flex items-center gap-3 mb-4">
                   <div className="h-8 w-8 rounded-lg bg-foreground text-background flex items-center justify-center">
                     <Info className="h-4 w-4" />
                   </div>
                   <h2 className="text-2xl font-bold font-display m-0">Trademarks & Brands</h2>
                </div>
                <p className="text-lg text-foreground/80 leading-relaxed">
                  Any references to third-party trademarks, brand names, products, services, or companies are used strictly for identification, commentary, or informational purposes only. Such references do not imply any affiliation, endorsement, sponsorship, or partnership with the respective trademark or brand owners.
                </p>
                <p className="mt-4 text-lg text-foreground/80 leading-relaxed">
                  All trademarks, logos, service marks, and registered marks mentioned on this website are the property of their respective owners. Their inclusion does not grant any license or right to use them beyond what is permitted under applicable laws.
                </p>
              </div>

              <div id="accuracy" className="mb-16">
                 <div className="bg-surface rounded-4xl border border-border p-8 md:p-12">
                    <h2 className="text-2xl font-bold font-display mt-0">Accuracy & Liability</h2>
                    <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
                      While we strive to ensure that all information presented on this website is accurate and up to date, we make no warranties or representations of any kind regarding the completeness, reliability, or accuracy of any content. Any reliance you place on such information is strictly at your own risk.
                    </p>
                    <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                      This website and its owners shall not be held liable for any loss, damage, or inconvenience arising from the use of this website or reliance on its content.
                    </p>
                 </div>
              </div>

              <div id="contact" className="mb-16">
                <div className="flex items-center gap-3 mb-6">
                   <div className="h-8 w-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center">
                     <Mail className="h-4 w-4" />
                   </div>
                   <h2 className="text-2xl font-bold font-display m-0">Rights & Takedown Requests</h2>
                </div>
                <p className="text-lg text-foreground/80 leading-relaxed">
                  If you are a trademark owner, copyright holder, or an authorized representative and believe that any content on this website infringes upon your intellectual property rights, please contact us promptly through our official channels.
                </p>
                <div className="mt-10 p-1 bg-gradient-warm rounded-3xl">
                  <div className="bg-card rounded-[1.4rem] p-8 text-center">
                    <h3 className="font-display text-xl font-bold">Need to reach us?</h3>
                    <p className="mt-2 text-muted-foreground">We take legal concerns seriously and will respond promptly.</p>
                    <Link 
                      to="/contact" 
                      className="mt-6 inline-flex items-center justify-center rounded-full bg-foreground px-10 py-4 text-sm font-bold text-background transition-transform hover:scale-105"
                    >
                      Go to Contact Page
                    </Link>
                  </div>
                </div>
                <p className="mt-10 text-sm text-muted-foreground leading-relaxed">
                  Upon receiving a valid request, we will review the matter and take appropriate action, including removal or modification of the content where necessary, in accordance with applicable laws.
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>
    </>
  );
}
